// eslint-disable-next-line @altermaind/you-shall-not-pass
import { AccountStatus } from '@/swagger/constants/anagraphics-enums';

export const en = {
  people: {
    pageTitle: 'People',
    noCompanyData: {
      title: 'Overview',
      description:
        'No profiles have been added yet. Sync your people’s information to start using the platform with your company.',
      boxAlert: {
        title: 'Add profiles',
        description:
          'Choose how you want to add employees: you can upload a file with multiple records or enter them manually, one at a time.',
        buttonLabel: 'Add',
      },
    },
    recapPeople: {
      current: {
        title: 'Total profiles',
        tooltip:
          'Total number of profiles uploaded and saved on the platform. You can add new profiles at any time using the “+” button.',
      },
      validProfile: {
        title: 'Valid profiles',
        tooltip:
          'Profiles complete with all required information. Only valid profiles can receive the assessment.',
      },
      invalidProfile: {
        title: 'Profiles to fix',
        tooltip:
          'Profiles with missing or incorrect data. Until they are updated, these users will not be able to receive the assessment.',
      },
      cta: {
        fixProfiles: 'Review and correct',
      },
      noInvalid: {
        text1: 'All profiles have been uploaded correctly! ',
        text2: 'Proceed to send the assessment',
      },
      survey: {
        title: 'Send assessment',
        description:
          'Start measuring hard and soft skills of employees and gather the first insights about your organization.',
        buttonLabel: 'Send Assessment',
      },
    },
    usefullInformations: {
      title: 'Useful materials',
      boxTutorial: {
        title: 'Tutorial',
        description: 'Learn how to set up the Records section to get started right away',
        buttonLabel: 'Watch now',
      },
      boxTemplate: {
        title: 'Profile template',
        description:
          "Don't have a file? Download our template and fill it with your company's data.",
        buttonLabel: 'Download',
      },
    },
    status: {
      [AccountStatus.ENABLED]: 'Active',
      [AccountStatus.PENDING]: 'Pending',
      [AccountStatus.DISABLED]: 'Disabled',
      [AccountStatus.NOT_INVITED]: 'Not active',
    },
    tableEmployees: {
      title: 'Profiles list',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      role: 'Company role',
      department: 'Department',
      status: {
        label: 'Status',
        tooltip:
          'User status\nNot active - profile that has not yet been sent the assessment\nPending - profile invited but not yet active (expired invitation or registration without completed assessment)\nActive - registered user\nDisabled - profile disabled by HR (e.g., leave or termination)',
      },
      bulkActionAssignDepartment: 'Assign department',
      assignDepartmentBulkDialog: {
        title: 'Assign department',
        subtitle: 'Select an existing department or create a new one',
        selectedCount: '({{count}} employees selected)',
        existingDepartment: 'Existing department',
        newDepartment: 'New department',
        selectDepartmentLabel: 'Department',
        selectDepartmentPlaceholder: 'Select department',
        newDepartmentNameLabel: 'Department name',
        newDepartmentNamePlaceholder: 'Enter name',
        newDepartmentDescription:
          'Define the department name and an optional manager, selectable only from employees not assigned to any department or team.',
        managerLabel: 'Manager (optional)',
        managerPlaceholder: 'Select a manager',
        infoAlert:
          'Changes related to Team and Managers must be made individually within each employee profile.',
        assign: 'Assign',
        duplicateDepartmentName:
          'Name already in use. Enter a different name or go back to select an existing department.',
        assignCheckWarnings: {
          departmentBecomesEmpty: {
            singular:
              'By assigning the selected employees, the department "{{name}}" will have no members and will be automatically deleted.',
            plural:
              'By assigning the selected employees, the departments "{{name}}" will have no members and will be automatically deleted.',
          },
          departmentLosesManager: {
            singular: 'The department "{{name}}" will be left without a manager.',
            plural: 'The departments "{{name}}" will be left without a manager.',
          },
        },
      },
    },
    filters: {
      fields: {
        roles: 'Roles',
        departments: 'Departments',
      },
      cta: {
        apply: 'Save',
        reset: 'Reset',
        cancel: 'Cancel',
      },
      unknownDepartment: 'Not assigned',
    },
    details: {
      tabs: {
        tab1: 'Profile',
        tab2: 'Assessment',
        tab3: 'Credits',
      },
      assessment: {
        title: '{{name}} assessment',
        emptyState: {
          title: 'Assessment',
          description: 'No {{name}} assessment has been sent yet.',
        },
        alertBadge:
          'The model is currently undergoing scientific validation and demonstrates a consistent level of reliability, especially in aggregated views for teams and departments.',
        status: {
          completed: 'Completed on {{date}}',
          notCompleted: 'Sent',
          noSurvey: 'Not sent',
          tooltip: {
            completed: 'The assessment has been successfully completed by the user.',
            notCompleted: 'The assessment has been sent and is awaiting completion by the user.',
            noSurvey: 'The assessment has not yet been sent to the user.',
          },
        },
        surveyTitle: {
          completed: 'The average score for {{name}}',
          notCompleted: 'Score {{name}}',
        },
        userNotValid: {
          title: 'Assessment',
          description:
            'The user is not valid, please correct the data to send the first questionnaire.',
        },
        descriptionNotSent:
          'The assessment has not yet been sent to this user. Proceed to send it to start the journey.',
        descriptionCompleted:
          'The chart shows the average scores (scale 0-7) for the {{metricsCount}} {{templateName}} considered essential to effectively face AI challenges.',
        descriptionNotCompleted:
          'The assessment has been sent but the user has not yet completed it. To prompt completion, go to the {{templateName}} Assessment section and click on "Send reminder".',
        goToSurvey: 'Go to questionnaire',
        sendSurvey: 'Send assessment',
        descriptionTitle: 'Description',
        discoverModel: 'Discover the model',
      },
      credits: {
        tabs: {
          income: 'Income',
          spent: 'Expenses',
        },
        nextRecharge: 'Credit renewal: !{DATE}! ({{days}} days remaining)',
        total: {
          label: 'Total available balance',
          currentMonth: 'Credits assigned this month',
        },
        overall: 'Credit usage',
        overallTooltip:
          'The percentage shows the share of credits used compared to the total allocation per user. Since credits accumulate for three months, actual monthly usage may exceed 100%, even though displayed values will never surpass that limit.',
        trend: 'Credit usage trend',
        averageMonthlyUsage: 'Average monthly usage',
        averageMonthlyUsageTooltip:
          'Indicates how many credits the employee uses on average per month. This metric helps understand platform usage intensity at an individual level.',
        fromNow: 'as of {{date}}',
        trendLastMonths: 'Last month trend',
        trendLastMonthsTooltip:
          'Shows the percentage increase or decrease in average usage per person compared to the previous month. Useful to spot growth or decline trends.',
        versusPreviousMonth: 'vs previous month',
        creditLabel: 'credits',
        history: {
          table: {
            title: 'Credit history',
            activity: 'Activity',
            date: 'Date',
            credits: 'Credits',
          },
          filters: {
            title: 'Filters',
            buttons: {
              save: 'Save',
              reset: 'Reset',
            },
            month: 'Month',
            year: 'Year',
          },
        },
        category: {
          filters: {
            label: 'Filter by',
            monthly: 'Last month',
            quarterly: 'Last 3 months',
            semester: 'Last 6 months',
          },
        },
        lastThreeMonths: {
          table: {
            title: 'Remaining credits in the last quarter',
            month: 'Month',
            accumulatedCredits: 'Accumulated credits',
            receivedCredits: 'Received credits',
            residualCredits: 'Residual credits',
            spentCredits: 'Spent credits',
            balance: 'Balance',
            balanceTooltip: 'Currently available credits',
          },
        },
        incomeAndSpent: {
          noData: 'No data available',
          title: 'Credit distribution by activity',
          tooltip:
            'The data show the overall distribution of credits spent and earned in the last month. The percentage indicates the average weight of each activity on the total.',
        },
        emptyState: {
          title: 'Credits',
          description:
            'User {{name}} is not yet active on Slash or has not been assigned credits.\nCredits will be displayed once the user becomes active and is invited to the questionnaire.',
        },
      },
    },
    tutorial: {
      title: 'Manage profiles',
      slides: {
        slide1: {
          title: 'Add profiles',
          description:
            'To upload contact records, click the “Add” button or the “+” icon and upload the file, or proceed manually. Make sure each profile includes the following fields: first name, last name, and email. AI will automatically support you in organizing and reading the data.',
        },
        slide2: {
          title: 'View profiles list',
          description:
            'Access the complete list of employees and check user status (active, suspended, inactive, disabled). Verify how many profiles are complete and how many require corrections before proceeding.',
        },
        slide3: {
          title: 'Review and correct',
          description:
            'Click the “Review and correct” button to fix profiles with missing or incorrect data. Only complete profiles can receive the assessment.',
        },
        slide4: {
          title: 'Profile details',
          description:
            'By opening each employee’s profile, you can access a complete view of their information. In “Profile,” you will find the user’s status, personal details, employment information, and personal data, along with links to their assessments.',
        },
      },
    },
    importCompanyData: {
      wizard: {
        setData: {
          title: 'Upload profiles',
          confirm: 'Next',
          previous: 'Profiles',
        },
        verifyData: {
          title: 'Review and correct',
          confirm: 'Confirm',
          previous: 'Back',
        },
      },
      setData: {
        title: 'Import company profiles',
        subtitle:
          'Upload the file with the profiles to add or update.\nMake sure each profile includes the following fields: First Name, Last Name, Email.',
        warning:
          'You can also add the following optional data to make the analyses more accurate: Date of birth (dd-mm-yyyy), Job title, Department, Manager (first name, last name, email), Team, Tax code, Job level.',
        downloadFile:
          'Don’t have a similar file?\n!{DOWNLOAD_FILE}! and fill it in with your company’s data',
        downloadFileTitle: 'Don’t have a file?',
        downloadLabel: 'Download our template',
        downloadFileDescription: 'Download our template and fill it with your company’s data',
        uploadedFiles: 'Uploaded document',
        loadingFile: {
          title: 'AI is verifying uploaded data',
          description: 'Profiles will soon appear on the platform',
        },
        infoBox: 'For information or support with the upload, check the !{DOCS}! or !{CONTACT}!',
        labelDocs: 'guide',
        labelContact: 'contact us',
        warningSaveBeforeQuit:
          '<b>Warning</b> If you leave without completing the review and correction of the employee records, the uploaded file will not be saved. Make sure to complete the operation before leaving.',
      },
      verifyData: {
        title: 'Verify profiles',
        subtitle:
          'Here you can find a summary of the uploaded profiles. You can confirm them as they are or correct missing data: only complete profiles will be eligible to receive the questionnaire. You can always edit them later in the “People” section.',
        dialogConfirmNavigateBack: {
          title: 'Unsaved profiles',
          description: 'Leaving this step will discard all information entered so far.',
          explicitQuestion: 'Are you sure you want to proceed?',
          cancel: 'Cancel',
          confirm: 'Yes, go back',
        },
        editImportedProfiles: {
          title: 'Profiles list',
          subtitle:
            "Here you'll find the list of profiles with issues. Edit them to proceed with sending the assessment.\nRemember: only first name, last name and email are required.\nOther fields are optional and can be completed or modified later.",
          cta: {
            confirm: 'Confirm profiles',
            deleteImportAndGoBack: 'Delete and go back',
          },
          table: {
            name: 'First name',
            surname: 'Last name',
            email: 'Email',
            birthDate: 'Date of birth',
            role: 'Company role',
            department: 'Department',
            manager: 'Manager email',
            area: 'Area (team)',
            fiscalCode: 'Tax code',
            jobLevel: 'Job level',
            filters: {
              title: 'Filters',
              cta: {
                close: 'Close',
              },
            },
          },
          infoBox: 'For more information, check the !{DOCS}! or !{CONTACT}!',
          labelDocs: 'guide',
          labelContact: 'contact us',
          filters: {
            importedProfileCompleted: 'Complete profiles',
            importedProfileIncompleted: 'Incomplete profiles',
            importedProfileAll: 'All profiles',
            cta: {
              submit: 'Apply',
              reset: 'Reset',
            },
          },
          successFeedback: 'Changes saved successfully!',
        },
      },
    },
    dialogPendingImportEmployees: {
      title: 'Pending upload',
      description:
        'A file has been uploaded but not yet confirmed.\nDecide how to proceed to access the People section.',
      cancel: 'Delete and upload a new file',
      confirm: 'Confirm and continue',
    },
    dialogImportEmployeesStillRunning: {
      title: 'AI is verifying uploaded data',
      description: 'Profiles will soon appear on the platform',
      confirm: 'Go to dashboard',
      cancel: 'Cancel and go back',
    },
    addEmployee: {
      choiceDialog: {
        title: 'Add new users',
        subtitle: 'Choose how you want to add new users to the platform',
        manual: {
          label: 'Enter manually',
          description: 'Add one user at a time by filling in their details.',
        },
        import: {
          label: 'Import file',
          description: 'Upload a file to add multiple users at once.',
        },
      },
      formDialog: {
        title: 'Add new users',
        subtitle: 'Enter the details of the new user',
        optionalDetails: 'Profile details (optional)',
        infoAlert:
          'By completing all fields, including optional ones, you allow the platform to generate more accurate analyses and a clearer view of the organization. By entering department, team and manager you will be able to view aggregated data by area and even more strategic insights.',
        roleInfo: 'Role information',
        personalDetails: 'Personal details',
        buttons: {
          cancel: 'Cancel',
          save: 'Save',
        },
        errors: {
          emailAlreadyInUse: 'The email entered is already in use within this organization.',
          emailAlreadyRegistered:
            'This email has already been registered on the platform, try with another email',
        },
      },
    },
    reviewAndFixEmployees: {
      title: 'Review and correct',
      description:
        'Here you can find the list of profiles that contain issues. Edit them to proceed with sending the assessment.\nNote: only the red fields need to be corrected.\nIf you have specified a manager, their email must already exist among the profiles; otherwise, the user will appear as needing correction.',
      table: {
        name: 'First name',
        surname: 'Last name',
        email: 'Email',
        birthDate: 'Date of birth',
        role: 'Company role',
        department: 'Department',
        manager: 'Manager email',
        area: 'Area (team)',
        fiscalCode: 'Tax code',
        jobLevel: 'Job level',
      },
      successFeedback: 'Changes saved successfully!',
    },
  },
};
